import { ContextConsumer } from '@lit/context';
import { localized, msg } from '@lit/localize';
import { t } from '../../../../services/runtime-i18n';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { when } from 'lit/directives/when.js';
import urlJoin from 'url-join';
import { ProductAPIMap } from '../../../../constants';
import { menuDataContext, menuErrorContext } from '../../../../context/menu-data';
import { DirtyFormController } from '../../../../controllers/dirty-form-controller';
import { flagContext } from '../../../../feature-flags';
import {
  APIFeatureDefinition,
  APIInformation,
  APIMenuDefinition,
  APIMenuItem,
  APITagItem,
  ProductSlug,
} from '../../../../types';
import '../../../tag/tag';
import styles from '../menu.styles.scss';

export const API_MENU_TAG = 'platform-api-menu' as const;

/**
 * Default Admin menu.
 */
@customElement(API_MENU_TAG)
@localized()
export class APIProductMenu extends LitElement {
  static override styles = [styles];

  @property()
  product = '';

  private apiInfo?: APIInformation;

  dirtyFormController = new DirtyFormController(this);
  _menuData = new ContextConsumer(this, { context: menuDataContext, subscribe: true });
  _menuErrors = new ContextConsumer(this, { context: menuErrorContext, subscribe: true });
  _flags = new ContextConsumer(this, { context: flagContext, subscribe: true });

  override render() {
    if (this.apiInfo == undefined && this.product != undefined) {
      this.apiInfo = ProductAPIMap[this.product as ProductSlug];
    }
    if (this.apiInfo === undefined) {
      return html`<menu><li>${msg('Loading...')}</li></menu>`;
    }
    const allMenus = this._menuData.value ?? {};
    if (!Object.keys(allMenus).includes(this.product)) {
      // A failed fetch is not a slow one: without this the menu sits on
      // "Loading..." forever, which reads as a hang rather than an error.
      if (this._menuErrors.value?.[this.product as ProductSlug]) {
        return html`<menu>
          <li>${msg('Unable to load the menu. Close and reopen it to try again.')}</li>
        </menu>`;
      }
      return html`<menu><li>${msg('Loading...')}</li></menu>`;
    }
    const menuData = allMenus[this.product as ProductSlug] as APIMenuDefinition;

    // Filter features based on feature flags
    const visibleFeatures = menuData.features?.filter((feat: APIFeatureDefinition) => {
      if (feat.featureFlag) {
        return this._flags.value?.[feat.featureFlag] ?? false;
      }
      return true;
    });

    return html`${visibleFeatures?.map((feat: APIFeatureDefinition) =>
      this.renderFeature(feat, this.apiInfo?.baseURL ?? ''),
    )}`;
  }

  private renderFeature(feat: APIFeatureDefinition, baseURL: string): TemplateResult<1> {
    const featureLink = feat.absolutePath ? urlJoin('/', feat.link || '') : urlJoin(baseURL, feat.link || '');
    const renderTitle = html`
      ${when(
        feat.link != undefined,
        () =>
          html`<h3>
            <a href=${featureLink}> ${t(feat.id, feat.name ?? '')} ${this.renderTags(feat.tags)} </a>
          </h3>`,
        () => html`<h3>${t(feat.id, feat.name ?? '')} ${this.renderTags(feat.tags)}</h3>`,
      )}
    `;

    // Filter menu items based on feature flags
    const visibleItems = feat.items?.filter((item: APIMenuItem) => {
      if (item.featureFlag) {
        return this._flags.value?.[item.featureFlag] ?? false;
      }
      return true;
    });

    const renderMenuItem = (item: APIMenuItem) => {
      const itemLink = item.absolutePath ? urlJoin('/', item.link) : urlJoin(baseURL, item.link);
      return html` <li>
        <a data-bt="${'navItem-' + item.id}" target=${item.openExternal ? '_blank' : '_self'} href=${itemLink}>
          ${t(item.id, item.name)} ${this.renderTags(item.tags)}
        </a>
      </li>`;
    };

    return html`
      ${when(
        feat.name != undefined,
        () => html`${renderTitle}`,
        () => nothing,
      )}
      ${when(
        visibleItems && visibleItems.length > 0,
        () => html` <menu>${visibleItems.map(renderMenuItem)}</menu>`,
        () => nothing,
      )}
    `;
  }

  private renderTags(tags?: APITagItem[]): TemplateResult<1> | typeof nothing {
    if (tags == undefined) {
      return nothing;
    }
    return html`${tags.map(
      (tag) =>
        html`${choose(
          tag.name,
          [
            ['new', () => html`<platform-tag variant="new">${msg('New')}</platform-tag>`],
            ['preview', () => html`<platform-tag variant="preview">${msg('Preview')}</platform-tag>`],
            ['early access', () => html`<platform-tag variant="early-access">${msg('Early Access')}</platform-tag>`],
          ],
          () => html``,
        )}`,
    )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [API_MENU_TAG]: APIProductMenu;
  }
}
